import { atom, getDefaultStore, useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { COMPANY_SWITCHED_EVENT } from '~/hooks/useActiveCompany';
import { SubmenuSection } from '~/types/sidebar';

type MenuOptions =
  | 'banks'
  | 'outstanding'
  | 'invoices'
  | 'support'
  | 'settings'
  | 'bookkeeping'
  | 'cashflow'
  | 'analytics'
  | 'aichat';

interface ActiveSubmenuContent {
  title: string;
  sections: SubmenuSection[];
}

const submenuAtom = atom<MenuOptions | null>(null);
const submenuContentAtom = atom<ActiveSubmenuContent | null>(null);

export const useIsSubmenuOpen = (submenu: MenuOptions): boolean => useAtomValue(submenuAtom) === submenu;
export const useIsAnySubmenuOpen = () => useAtomValue(submenuAtom) !== null;
export const useActiveSubmenuContent = () => useAtomValue(submenuContentAtom);

const setSubmenuAtom = atom(null, (_get, set, newSubmenu: MenuOptions | null) => {
  set(submenuAtom, newSubmenu);
});

const closeSubmenuAtom = atom(null, (_get, set) => {
  set(submenuAtom, null);
  set(submenuContentAtom, null);
});

const setActiveSubmenuContentAtom = atom(null, (_get, set, content: ActiveSubmenuContent | null) => {
  set(submenuContentAtom, content);
});

export const useSetSubmenu = (submenu: MenuOptions | null) => {
  const setSubmenu = useSetAtom(setSubmenuAtom);
  return useCallback(() => setSubmenu(submenu), [setSubmenu, submenu]);
};
export const useSetActiveSubmenuContent = () => useSetAtom(setActiveSubmenuContentAtom);
export const useCloseSubmenu = () => useSetAtom(closeSubmenuAtom);

// Close the sidebar submenu when the company changes. The submenu content is
// company-scoped (sections depend on the user's permissions for that company),
// so leaving it open would show stale sections that the user may not be allowed
// to access in the newly selected company.
window.addEventListener(COMPANY_SWITCHED_EVENT, () => {
  const store = getDefaultStore();
  store.set(submenuAtom, null);
  store.set(submenuContentAtom, null);
});
